// CareBot 开通转化相关类型定义
// 与 Rails BFF /api/carebot/* 接口契约对齐
// 参考: /Users/showlew/.claude/plans/implementation-plan-icope-carebot-webhook.md 第 3、5.3 节

/**
 * 顾问触发 CareBot 开通的请求体
 * Rails 侧会基于 userId/assessmentId/needReportId 组装 webhook payload 转发到 CareBot
 *
 * 现场补填手机号场景：当 user.phone 为空时，Modal 让顾问输入手机号 + SMS 验证码，
 * 一并随 onboard 请求发送（elderPhone + smsCode 必须同时提供，单独提供其一会 422）。
 * Rails 验证 smsCode 通过后，elderPhone 优先于 user.phone 用于 CareBot payload，
 * 并在 user.phone 原本为空时回写到 ICOPE 数据库。
 */
export interface CareBotOnboardRequest {
  userId: string | number;
  assessmentId: string | number;
  needReportId?: string | number;
  elderPhone?: string;
  smsCode?: string;
}

/**
 * 老人 CareBot 激活状态（GET /api/carebot/onboarding-status）
 * 仅用于 Button 决定文案"一键开通" vs "已开通 · 查看二维码"。
 * Modal 完整数据（claimUrl/expiresAt/subscription）仍走 onboard 幂等再调获取。
 */
export interface CareBotOnboardingStatus {
  activated: boolean;
  lastOnboardedAt?: string;
  shortCode?: string;
}

/**
 * 老人手机号发码响应（POST /api/carebot/send-sms）
 */
export interface CareBotSendSmsResponse {
  ttlSeconds: number;        // 验证码 TTL（5 分钟）
  cooldownSeconds: number;   // 下次发码冷却（1 分钟）
}

/**
 * Elder claim 信息（老人扫码认领凭证）
 */
export interface CareBotElderClaim {
  shortCode: string;        // 老人现场可读的短码，如 "A1B2C3"
  claimUrl: string;         // 老人扫码进入的认领链接
  expiresAt: string;        // ISO 时间，邀请过期时间
}

/**
 * 订阅状态信息
 */
export interface CareBotSubscriptionInfo {
  status: 'active' | 'inactive' | 'expired';
  tier: 'trial' | 'basic' | 'premium' | string;
  expiresAt: string;
}

/**
 * Elder 创建/复用结果
 */
export interface CareBotElderInfo {
  elderId: string;
  familyId: string;
  isExisting: boolean;
}

/**
 * ICOPE Report 索引
 */
export interface CareBotIcopeReportInfo {
  reportId: string;
  status?: 'ready' | string;
}

/**
 * Rails 透传 CareBot webhook 的成功响应（已解包外层 ApiResponse.data）
 */
export interface CareBotOnboardResponse {
  ok: boolean;
  elder: CareBotElderInfo;
  icopeReport: CareBotIcopeReportInfo;
  elderClaim: CareBotElderClaim;
  subscription: CareBotSubscriptionInfo;
}

/**
 * 顾问代发家属邀请请求
 */
export interface CareBotFamilyInviteRequest {
  assessmentId: string | number;
  elderPhone: string;
  familyPhone: string;
}

/**
 * 家属邀请凭证
 */
export interface CareBotFamilyInvite {
  shortCode: string;
  inviteUrl: string;
  expiresAt: string;
}

/**
 * 家属邀请响应
 */
export interface CareBotFamilyInviteResponse {
  ok: boolean;
  familyInvite: CareBotFamilyInvite;
  smsSent: boolean;          // CareBot 是否真实发了短信；未发时前端展示链接给顾问转给家属
}

/**
 * Service 层抛出的统一业务错误
 * UI 直接展示 message（已转中文）
 */
export class CareBotOnboardingError extends Error {
  code: string;
  retryable: boolean;

  constructor(code: string, message: string, retryable = true) {
    super(message);
    this.name = 'CareBotOnboardingError';
    this.code = code;
    this.retryable = retryable;
  }
}
